'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import {
  getConsent,
  saveConsent,
  createConsentRecord,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  isOpen: boolean;
  onClose: () => void;
  region: ConsentRegion;
}

export default function ConsentPreferenceCenter({
  isOpen,
  onClose,
  region,
}: ConsentPreferenceCenterProps) {
  const [choices, setChoices] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  });
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Load current consent on open
  useEffect(() => {
    if (isOpen) {
      const current = getConsent();
      if (current) {
        setChoices(current.choices);
      }
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll(
      'button, [role="switch"], [tabindex]:not([tabIndex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    firstElement?.focus();
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleToggle = (category: keyof typeof choices) => {
    if (category === 'necessary') return;
    setChoices((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    const record = createConsentRecord(region, choices);
    saveConsent(record);
    logConsentAction('save_preferences', region);
    onClose();
  };

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('accept_all', region);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id="preferences-title" className="text-lg font-semibold text-gray-900">
            Cookie Preferences
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close preferences"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Necessary */}
          <div className="pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-900">Necessary</label>
              <button
                role="switch"
                aria-checked
                disabled
                className="w-12 h-6 bg-gray-900 rounded-full relative cursor-not-allowed"
              >
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Essential for core functionality, security, and fraud prevention. Always enabled.
            </p>
          </div>

          {/* Analytics */}
          <div className="pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-900">Analytics</label>
              <button
                role="switch"
                aria-checked={choices.analytics}
                onClick={() => handleToggle('analytics')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.analytics ? 'bg-gray-900' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.analytics ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Help us understand how you use our site to improve your experience.
            </p>
          </div>

          {/* Marketing */}
          <div className="pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-900">Marketing</label>
              <button
                role="switch"
                aria-checked={choices.marketing}
                onClick={() => handleToggle('marketing')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.marketing ? 'bg-gray-900' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.marketing ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Enable personalized ads and marketing campaigns tailored to your interests.
            </p>
          </div>

          {/* Preferences */}
          <div className="pb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="font-medium text-gray-900">Preferences</label>
              <button
                role="switch"
                aria-checked={choices.preferences}
                onClick={() => handleToggle('preferences')}
                className={`w-12 h-6 rounded-full relative transition-colors ${
                  choices.preferences ? 'bg-gray-900' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    choices.preferences ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Remember your preferences and personalize your experience across visits.
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Save Preferences
          </button>
          <button
            onClick={handleAcceptAll}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-colors"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
